/**
 * The boards: who is furthest along, overall, in each skill, and against each boss.
 *
 * Asked for when opened and whenever the board changes, never on a timer: a
 * board is read, not watched, and the world keeps each one for half a minute
 * anyway.
 */

import { useEffect, useState } from 'react';
import { levelForExperience, SKILL_NAMES, TICK_MS } from '@inverse/common';
import { api, storedToken } from '../net/api';
import { CloseButton } from './CloseButton';
import { useDraggableWindow } from './draggable';
import { SkillIcon } from './SkillIcon';

type Row = { name: string; value: number; extra: number | null };
type Board = { kind: 'overall' } | { kind: 'skill'; skill: number } | { kind: 'boss'; npc: number };

function seconds(ticks: number): string {
  const total = Math.round((ticks * TICK_MS) / 1000);
  const minutes = Math.floor(total / 60);
  return minutes > 0 ? `${minutes}:${String(total % 60).padStart(2, '0')}` : `${total}s`;
}

export function Leaderboards({ me, onClose }: { me: string; onClose: () => void }): JSX.Element {
  const { attach, style, onPointerDown } = useDraggableWindow('leaderboards');
  const [board, setBoard] = useState<Board>({ kind: 'overall' });
  // What came back, and which board it was for, so a board still being read
  // shows as being read rather than as the last one.
  const [answer, setAnswer] = useState<{ key: string; rows: Row[] | null; problem: string }>({
    key: '',
    rows: null,
    problem: '',
  });
  const [bosses, setBosses] = useState<{ id: number; name: string }[]>([]);
  const key =
    board.kind === 'skill'
      ? `skill:${board.skill}`
      : board.kind === 'boss'
        ? `boss:${board.npc}`
        : 'overall';
  const rows = answer.key === key ? answer.rows : null;
  const problem = answer.key === key ? answer.problem : '';

  useEffect(() => {
    const token = storedToken();
    if (!token) return;
    api
      .bosses(token)
      .then((held) => setBosses(held.bosses))
      .catch(() => setBosses([]));
  }, []);

  useEffect(() => {
    const token = storedToken();
    if (!token) return;
    let current = true;
    const asked =
      board.kind === 'skill'
        ? api.leaderboard(token, 'skill', board.skill)
        : board.kind === 'boss'
          ? api.leaderboard(token, 'boss', board.npc)
          : api.leaderboard(token, 'overall');
    asked
      .then((held) => {
        if (current) setAnswer({ key, rows: held.rows, problem: '' });
      })
      .catch(() => {
        if (current)
          setAnswer({ key, rows: null, problem: 'The boards could not be read just now.' });
      });
    return () => {
      current = false;
    };
  }, [board, key]);

  const heading =
    board.kind === 'skill'
      ? ['Level', 'Experience']
      : board.kind === 'boss'
        ? ['Kills', 'Quickest']
        : ['Total level', 'Experience'];

  return (
    <div className="trade leaderboards" ref={attach} style={style}>
      <div className="panel-head drag-handle" onPointerDown={onPointerDown}>
        <span>Leaderboards</span>
        <CloseButton onClose={onClose} />
      </div>
      <div className="leaderboard-pick">
        <select
          value={key}
          onChange={(event) => {
            const [kind, of] = event.target.value.split(':');
            const number = Number(of);
            setBoard(
              kind === 'skill'
                ? { kind: 'skill', skill: number }
                : kind === 'boss'
                  ? { kind: 'boss', npc: number }
                  : { kind: 'overall' },
            );
          }}
        >
          <option value="overall">Overall</option>
          <optgroup label="Skills">
            {SKILL_NAMES.map((name, skill) => (
              <option key={name} value={`skill:${skill}`}>
                {name}
              </option>
            ))}
          </optgroup>
          {bosses.length > 0 ? (
            <optgroup label="Bosses">
              {bosses.map((boss) => (
                <option key={boss.id} value={`boss:${boss.id}`}>
                  {boss.name}
                </option>
              ))}
            </optgroup>
          ) : null}
        </select>
        {board.kind === 'skill' ? <SkillIcon skill={SKILL_NAMES[board.skill] ?? ''} /> : null}
      </div>
      <div className="panel-body leaderboard-body">
        {problem ? <p className="hint warning">{problem}</p> : null}
        {rows === null && !problem ? <p className="hint">Reading the board.</p> : null}
        {rows && rows.length === 0 ? (
          <p className="hint">
            {board.kind === 'boss' ? 'Nobody has killed it yet.' : 'Nobody is on this board yet.'}
          </p>
        ) : null}
        {rows && rows.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>{heading[0]}</th>
                <th>{heading[1]}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, at) => (
                <tr key={row.name} className={row.name === me ? 'mine' : undefined}>
                  <td>{at + 1}</td>
                  <td>{row.name}</td>
                  <td>
                    {board.kind === 'skill'
                      ? levelForExperience(row.value)
                      : row.value.toLocaleString()}
                  </td>
                  <td>
                    {board.kind === 'skill'
                      ? Math.round(row.value).toLocaleString()
                      : board.kind === 'boss'
                        ? row.extra === null
                          ? ''
                          : seconds(row.extra)
                        : Math.round(row.extra ?? 0).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </div>
  );
}
